/**
 * i18next Configuration
 * 
 * Enterprise-grade internationalization setup with:
 * - Lazy loading of translation files
 * - Language detection from navigator (first-time); persistence via bls-store (localStorage)
 * - Namespace-based organization
 * - TypeScript support
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Define namespaces - each corresponds to a JSON file
const namespaces = ['common', 'auth', 'validation', 'liveness', 'appointments', 'booking', 'account', 'public', 'formTemplate', 'ratings'] as const;
export type I18nNamespace = typeof namespaces[number];

const isDev = import.meta.env.DEV;

/** Language from bls-store (localStorage) before i18n init. Used by Layout to reconcile after API returns. */
export const preInitLanguage: string | null = (() => {
  try {
    const raw = localStorage.getItem('bls-store');
    if (!raw) return null;
    return JSON.parse(raw)?.state?.currentLanguage || null;
  } catch {
    return null;
  }
})();

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    // Seed list so pre-init detection can pick up non-English (incl. RTL) on
    // first load before the backend list has resolved. Layout still hydrates
    // the authoritative list from GET /public/supported-languages at runtime.
    supportedLngs: ['en', 'fr', 'es', 'ar'],

    // Default namespace
    defaultNS: 'common',
    ns: namespaces,

    // Debug mode in development
    debug: isDev,

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Language detection: navigator for first-time users only; no cache (bls-store persists via Zustand)
    detection: {
      order: ['navigator'],
      caches: [],
    },

    // HTTP backend settings (load from /public/locales)
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // React specific settings
    react: {
      useSuspense: true, // Use React Suspense for loading
    },
  });

export default i18n;
